import { SerializedError } from '@reduxjs/toolkit';
import { FetchBaseQueryError } from '@reduxjs/toolkit/query';

function toDetailMessages(detail: unknown): string[] {
  if (typeof detail === 'string') {
    return detail.trim() ? [detail] : [];
  }
  if (Array.isArray(detail)) {
    return detail.filter(
      (item): item is string => typeof item === 'string' && item.trim() !== '',
    );
  }
  return [];
}

/**
 * Extracts a user-friendly error message from RTK Query errors
 *
 * This utility handles the two main types of errors that RTK Query can return:
 * - FetchBaseQueryError: API/HTTP errors with response data
 * - SerializedError: Network errors, timeouts, or other non-HTTP errors
 *
 * @param error - The error caught from an RTK Query mutation or query
 * @param fallbackMessage - Default message if no specific error is found
 * @returns User-friendly error message extracted from the error object
 * ```
 */
export function extractErrorMessage(
  error: unknown,
  fallbackMessage: string,
): string {
  // Type guard for FetchBaseQueryError (API errors)
  if (typeof error === 'object' && error !== null && 'data' in error) {
    const fetchError = error as FetchBaseQueryError;
    if (fetchError.data && typeof fetchError.data === 'object') {
      const apiError = fetchError.data as Record<string, unknown>;
      const messages = toDetailMessages(apiError.detail);
      if (messages.length > 0) {
        return messages.join('; ');
      }
      if (typeof apiError.message === 'string' && apiError.message) {
        return apiError.message;
      }
      return fallbackMessage;
    }
  }
  // Type guard for SerializedError (network/other errors)
  else if (typeof error === 'object' && error !== null && 'message' in error) {
    const serializedError = error as SerializedError;
    if (serializedError.message) {
      return serializedError.message;
    }
  }

  return fallbackMessage;
}

/**
 * Reads the HTTP status code off an RTK Query error.
 *
 * RTK Query types caught errors as `unknown` (and a `FetchBaseQueryError`'s
 * `status` can be a string code like `'FETCH_ERROR'` for network/timeout
 * failures). This narrows to the HTTP-status case, returning `undefined` for
 * everything else so callers can safely compare against a numeric status.
 *
 * @param error - The error from an RTK Query mutation/query result or catch.
 * @returns The numeric HTTP status, or `undefined` if there isn't one.
 */
export function getErrorStatus(error: unknown): number | undefined {
  if (
    typeof error === 'object' &&
    error !== null &&
    'status' in error &&
    typeof (error as { status: unknown }).status === 'number'
  ) {
    return (error as { status: number }).status;
  }
  return undefined;
}

/**
 * GMR's `detail` field is free text from transport.net.
 * Only messages reviewed and added here are shown to users as verbatim.
 * Anything else falls back to a generic message, but we can add more as we need.
 */
export const APPROVED_GMR_ERROR_MESSAGES: readonly string[] = [
  'Standby already activated in TCS',
  'Cannot Activate Trip - Not on Standby.',
  'Cannot Activate Trip - Activation Pending.',
  'There is another pending update! Failed to process the cancellation',
  'Contract ID does not match the original transport record',
  'The transport is already complete! Failed to process the cancellation',
];

function getSubmissionErrorMessage(statusCode?: number): string {
  return statusCode
    ? `Request could not be submitted (status ${statusCode}). Try again, or call to place air ambulance order.`
    : 'Request could not be submitted. Try again, or call to place air ambulance order.';
}

/**
 * User facing error for a failed etl requests to air ambulance.
 */
export function getEtlSubmissionErrorMessage(
  error: unknown,
  statusCode = getErrorStatus(error),
): string {
  const fallback = getSubmissionErrorMessage(statusCode);
  const message = extractErrorMessage(error, fallback);
  return APPROVED_GMR_ERROR_MESSAGES.includes(message) ? message : fallback;
}
